import React, { useMemo, useState } from 'react';

const SERVICE_OPTIONS = [
  'Website',
  'Mobile App',
  'Dashboard',
  'AI Voice Assistant',
  'QR Campaign',
  'SEO',
  'Social Media',
  'Automation',
  'Branding',
  'Business Email & Hosting',
  'Full Digital Launch Kit'
];

const OFFER_MATRIX = [
  {
    name: 'Website LaunchCare',
    match: ['Website', 'SEO', 'Branding'],
    price: '£199 deposit / project quote after preview',
    timeline: '3–10 days depending on scope',
    next: 'Request a website preview and conversion plan'
  },
  {
    name: 'Mobile App / App-Style System',
    match: ['Mobile App', 'Dashboard', 'Automation'],
    price: 'From £299+ depending on app flow',
    timeline: '7–21 days',
    next: 'Map the app screens and customer journey'
  },
  {
    name: 'AI Voice Assistant',
    match: ['AI Voice Assistant', 'Automation'],
    price: 'From £199+ setup depending on script and workflow',
    timeline: '3–7 days',
    next: 'Prepare FAQs, service list and booking/contact flow'
  },
  {
    name: 'QR Campaign System',
    match: ['QR Campaign', 'Social Media', 'Branding'],
    price: 'From £99 launch pack',
    timeline: '1–5 days',
    next: 'Create QR poster, flyer and campaign assets'
  },
  {
    name: 'CRM / Booking Dashboard',
    match: ['Dashboard', 'Automation'],
    price: 'From £299+ depending on workflow',
    timeline: '7–14 days',
    next: 'Define leads, bookings, jobs and customer records'
  },
  {
    name: 'Full Digital Launch Kit',
    match: ['Full Digital Launch Kit', 'Website', 'Mobile App', 'Dashboard', 'QR Campaign', 'Social Media', 'Automation'],
    price: 'From £299+ / custom package',
    timeline: '7–30 days',
    next: 'Build a full launch roadmap with preview and phased delivery'
  }
];

type SmartQuoteForm = {
  businessName: string;
  industry: string;
  town: string;
  currentWebsite: string;
  socialLink: string;
  services: string[];
  budget: string;
  urgency: string;
  goals: string;
  contactName: string;
  email: string;
  phone: string;
  notes: string;
};

const initialForm: SmartQuoteForm = {
  businessName: '',
  industry: '',
  town: '',
  currentWebsite: '',
  socialLink: '',
  services: [],
  budget: '£199–£499 starter budget',
  urgency: 'This month',
  goals: '',
  contactName: '',
  email: '',
  phone: '',
  notes: ''
};

export default function NexoraXSmartQuote() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState<SmartQuoteForm>(initialForm);
  const [submitted, setSubmitted] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');

  const recommendation = useMemo(() => {
    const selected = form.services;
    const best =
      OFFER_MATRIX
        .map((offer) => ({
          ...offer,
          score: offer.match.filter((item) => selected.includes(item)).length
        }))
        .sort((a, b) => b.score - a.score)[0] || OFFER_MATRIX[0];

    const hasWebsite = Boolean(form.currentWebsite.trim());
    const hasSocial = Boolean(form.socialLink.trim());
    const automationNeed = selected.includes('Automation') || selected.includes('AI Voice Assistant') || selected.includes('Dashboard');
    const conversionNeed = selected.includes('Website') || selected.includes('SEO') || selected.includes('QR Campaign');

    const onlineScore = hasWebsite ? 72 : 38;
    const conversionScore = conversionNeed ? 86 : 62;
    const automationScore = automationNeed ? 91 : 55;
    const leadScore = form.town ? 82 : 60;
    const overall = Math.round((onlineScore + conversionScore + automationScore + leadScore) / 4);

    const upsell = selected.includes('Website')
      ? 'AI Voice Assistant or QR Campaign Pack'
      : selected.includes('QR Campaign')
        ? 'Website LaunchCare landing page'
        : selected.includes('Dashboard')
          ? 'Automation follow-up system'
          : 'Website LaunchCare + Social Launch Pack';

    return {
      mainService: best.name,
      priceBand: best.price,
      timeline: best.timeline,
      nextAction: best.next,
      upsell,
      overall,
      onlineScore,
      conversionScore,
      automationScore,
      leadScore,
      why: `${form.industry || 'This business'} in ${form.town || 'your area'} looks best suited for ${best.name} because the selected goals point toward ${selected.join(', ') || 'a stronger digital presence'}.`
    };
  }, [form]);

  const toggleService = (service: string) => {
    setForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((item) => item !== service)
        : [...prev.services, service]
    }));
  };

  const submitBrief = async (status = 'New') => {
    const safeBusinessName = form.businessName?.trim() || "Free NexoraX Preview Request";

    if (!form.contactName || !form.email) {
      setNotice("Please add your name and email before sending the brief.");
      return;
    }

    setSaving(true);
    setNotice('');

    try {
      const response = await fetch('/api/smart-quotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
        ...form,
        businessName: safeBusinessName,
        industry: form.industry || "TikTok Lead",
        town: form.town || "Online",
        services: form.services?.length ? form.services : ["Free Digital Preview"],
        budget: form.budget || "Starter preview",
        urgency: form.urgency || "Callback requested",
        source: "tiktok",
        campaign: "tiktok_smart_quote_traffic_test",
        recommendation,
        status,
      })
      });

      const data = await response.json();
      if (!data.success) throw new Error(data.error || 'Save failed.');

      setSubmitted(data.brief);
      setStep(5);
    } catch {
      setNotice('Free preview request could not be sent. Please try again or call 08000 622827.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <main className="min-h-screen overflow-hidden bg-[#02030a] text-white">

      <style>{`
        @keyframes nxGlowPulseSoft {
          0%, 100% {
            text-shadow: 0 0 0 rgba(34,211,238,0);
            opacity: 0.96;
          }
          50% {
            text-shadow: 0 0 14px rgba(34,211,238,0.28), 0 0 26px rgba(34,211,238,0.14);
            opacity: 1;
          }
        }

        @keyframes nxGlowPulseStrong {
          0%, 100% {
            text-shadow: 0 0 0 rgba(34,211,238,0);
            opacity: 0.96;
          }
          50% {
            text-shadow: 0 0 20px rgba(34,211,238,0.42), 0 0 36px rgba(34,211,238,0.2);
            opacity: 1;
          }
        }

        @keyframes nxPanelSweep {
          0% {
            transform: translateX(-140%) skewX(-18deg);
            opacity: 0;
          }
          18% {
            opacity: 0.10;
          }
          50% {
            opacity: 0.18;
          }
          100% {
            transform: translateX(180%) skewX(-18deg);
            opacity: 0;
          }
        }

        @keyframes nxFadeUp {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .nx-glow-soft {
          animation: nxGlowPulseSoft 3.8s ease-in-out infinite;
        }

        .nx-glow-strong {
          animation: nxGlowPulseStrong 4.4s ease-in-out infinite;
        }

        .nx-panel-sweep {
          animation: nxPanelSweep 7.5s ease-in-out infinite;
        }


        

        .nx-fade-up {
          animation: nxFadeUp 0.75s ease-out;
        }

        .nx-trust-chip {
          opacity: 1 !important;
          filter: none !important;
          isolation: isolate;
        }

        

        


        @keyframes nxTrustBadgeGlow {
          0%, 100% {
            box-shadow: 0 0 10px rgba(34,211,238,0.18);
            border-color: rgba(34,211,238,0.42);
          }
          50% {
            box-shadow: 0 0 24px rgba(34,211,238,0.45);
            border-color: rgba(103,232,249,0.9);
          }
        }

        @keyframes nxTrustBadgeSweep {
          0% { transform: translateX(-160%) skewX(-18deg); opacity: 0; }
          35% { opacity: 0.25; }
          100% { transform: translateX(180%) skewX(-18deg); opacity: 0; }
        }

        .nx-trust-badge {
          position: relative;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          padding: 0.5rem 0.9rem;
          border-radius: 999px;
          border: 1px solid rgba(34,211,238,0.5);
          background: linear-gradient(135deg, rgba(3,16,25,0.98), rgba(8,13,28,0.98), rgba(43,12,67,0.9));
          animation: nxTrustBadgeGlow 3.4s ease-in-out infinite;
        }

        .nx-trust-badge::before {
          content: "";
          position: absolute;
          inset: 0;
          width: 40%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          animation: nxTrustBadgeSweep 4.2s ease-in-out infinite;
          z-index: 1;
        }

        .nx-trust-badge-text {
          position: relative;
          z-index: 5;
          color: #ffffff !important;
          opacity: 1 !important;
          filter: none !important;
          text-shadow: 0 0 6px rgba(103,232,249,0.55);
          font-weight: 950;
          letter-spacing: 0.05em;
        }


        @keyframes nxTrustRingPulseFinal {
          0%, 100% {
            box-shadow: 0 0 12px rgba(34,211,238,0.18), inset 0 0 0 rgba(34,211,238,0);
            border-color: rgba(34,211,238,0.45);
          }
          50% {
            box-shadow: 0 0 28px rgba(34,211,238,0.45), inset 0 0 22px rgba(34,211,238,0.08);
            border-color: rgba(103,232,249,0.95);
          }
        }

        @keyframes nxTrustSweepFinal {
          0% { transform: translateX(-130%); opacity: 0; }
          35% { opacity: 0.22; }
          100% { transform: translateX(160%); opacity: 0; }
        }

        .nx-final-trust-row {
          display: flex;
          flex-wrap: wrap;
          gap: 0.55rem;
          align-items: center;
        }

        .nx-final-trust-badge {
          position: relative;
          isolation: isolate;
          overflow: hidden;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-height: 42px;
          padding: 0.55rem 0.95rem;
          border-radius: 999px;
          border: 1px solid rgba(34,211,238,0.55);
          background: linear-gradient(135deg, rgba(2,18,28,0.96), rgba(10,16,32,0.96), rgba(45,13,70,0.88));
          animation: nxTrustRingPulseFinal 3.4s ease-in-out infinite;
        }

        .nx-final-trust-badge::before {
          content: "";
          position: absolute;
          top: 0;
          bottom: 0;
          left: 0;
          width: 42%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
          animation: nxTrustSweepFinal 4.5s ease-in-out infinite;
          z-index: 1;
          pointer-events: none;
        }

        .nx-final-trust-text {
          position: relative;
          z-index: 5;
          color: #ffffff !important;
          opacity: 1 !important;
          filter: none !important;
          text-shadow: 0 0 8px rgba(34,211,238,0.35);
          font-weight: 950;
          letter-spacing: 0.06em;
          line-height: 1;
          white-space: nowrap;
        }


        @keyframes nxClearChipGlow {
          0%, 100% {
            box-shadow: 0 0 12px rgba(34,211,238,0.18);
            border-color: rgba(34,211,238,0.45);
          }
          50% {
            box-shadow: 0 0 24px rgba(34,211,238,0.38);
            border-color: rgba(103,232,249,0.95);
          }
        }

        .nx-clear-chip {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.55rem 0.95rem;
          border-radius: 999px;
          border: 1px solid rgba(34,211,238,0.55);
          background: linear-gradient(135deg, rgba(3,20,30,0.98), rgba(10,16,32,0.98), rgba(50,15,76,0.88));
          color: #ffffff !important;
          opacity: 1 !important;
          filter: none !important;
          text-shadow: none !important;
          font-weight: 950;
          letter-spacing: 0.06em;
          line-height: 1;
          white-space: nowrap;
          animation: nxClearChipGlow 3.2s ease-in-out infinite;
        }

        .nx-clear-chip:nth-child(2) {
          animation-delay: 0.25s;
        }

        .nx-clear-chip:nth-child(3) {
          animation-delay: 0.5s;
          color: #67e8f9 !important;
        }


        @keyframes nxCleanTrustPulseFinal {
          0%, 100% {
            box-shadow: 0 0 10px rgba(34,211,238,0.18);
            border-color: rgba(34,211,238,0.42);
          }
          50% {
            box-shadow: 0 0 24px rgba(34,211,238,0.42);
            border-color: rgba(103,232,249,0.92);
          }
        }
      `}</style>

      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_20%_10%,rgba(0,240,255,0.16),transparent_35%),radial-gradient(circle_at_80%_20%,rgba(168,85,247,0.18),transparent_35%),linear-gradient(180deg,#02030a,#050014_55%,#02030a)]" />
      <div className="pointer-events-none fixed inset-0 opacity-40 [background-image:linear-gradient(rgba(0,240,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.05)_1px,transparent_1px)] [background-size:42px_42px]" />

      <section className="relative mx-auto max-w-7xl px-5 py-10 md:py-16">
        <div className="rounded-[2rem] border border-cyan-400/35 bg-black/55 p-6 shadow-[0_0_40px_rgba(0,240,255,0.10)] backdrop-blur-xl md:p-10">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.45em] text-cyan-200">
                NexoraX Smart Quote Engine
              </p>
              <h1 className="mt-4 max-w-4xl text-4xl font-black leading-tight md:text-6xl">
                Get a free NexoraX preview before you pay.
              </h1>
              <p className="mt-5 max-w-3xl text-base leading-relaxed text-slate-100 md:text-lg">
                Send us your website, Facebook, TikTok, Instagram, app idea or business system. We’ll review it free and show what NexoraX could improve. No payment first.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-500/30 bg-purple-950/20 p-5 text-right">
              <p className="font-mono text-[10px] uppercase tracking-widest text-purple-200">Business advert mode</p>
              <p className="mt-2 text-2xl font-black text-cyan-200">NEX20</p>
              <p className="text-xs text-slate-200">20% launch code available</p>
              <a
                href="/client-preview"
                className="mt-4 inline-flex rounded-xl border border-cyan-500/40 bg-cyan-950/30 px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-white hover:bg-cyan-900/40"
              >
                Track Existing Brief
              </a>
            </div>
          </div>

          <div className="mt-8 grid gap-3 md:grid-cols-5">
            {['Business', 'Needs', 'Budget', 'Contact', 'Result'].map((label, index) => (
              <button
                key={label}
                type="button"
                onClick={() => setStep(index + 1)}
                className={`rounded-2xl border px-4 py-3 font-mono text-[10px] uppercase tracking-widest transition-all ${
                  step === index + 1
                    ? 'border-cyan-400 bg-cyan-950/40 text-white shadow-[0_0_20px_rgba(0,240,255,0.18)]'
                    : 'border-zinc-800 bg-black/40 text-slate-200 hover:border-purple-500/40'
                }`}
              >
                {index + 1}. {label}
              </button>
            ))}
          </div>
        </div>

        
        {/* TIKTOK_VISITOR_CONVERSION_STRIP */}
        <section className="relative overflow-hidden rounded-3xl border border-cyan-500/25 bg-gradient-to-r from-[#041b22] via-[#07111f] to-[#2a0a35] px-5 py-5 text-white shadow-[0_0_28px_rgba(34,211,238,0.18)]">
          <div className="pointer-events-none absolute inset-y-0 left-[-20%] w-[28%] bg-gradient-to-r from-transparent via-cyan-300/10 to-transparent blur-xl nx-panel-sweep" />

          <div className="relative z-10 flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-3xl">
              <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-300 font-black">
                Free Preview Offer Active
              </p>

              <h2 className="mt-3 text-2xl md:text-3xl font-black uppercase tracking-tight leading-tight text-white">
                <span className="text-cyan-200">NO CODE.</span>{' '}
                <span className="text-cyan-200">NO LOGIN.</span>{' '}
                <span className="text-cyan-300 drop-shadow-[0_0_12px_rgba(34,211,238,0.45)]">
                  JUST SEND YOUR LINK.
                </span>
              </h2>

<p className="mt-3 max-w-3xl text-sm md:text-base leading-relaxed text-slate-100 font-semibold normal-case tracking-normal">
                We do not ask for your website login, passwords or code files. Just send us your website, Facebook, TikTok, Instagram, app idea or dashboard link. NexoraX will review it free and send you a preview of what we could improve. If you like the preview, approve the next step. If not, no charge. Free examples can include Campaign Hub tracking, Client Preview Portal, QR / WhatsApp callback tracking and a basic launch dashboard. Coming soon: Nexus Search, Nexus Agent, Nexus Chat, Voice Matrix and UCG Studio.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => { setStep(1); setTimeout(() => document.getElementById("smart-quote-business-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); }}
                className="rounded-xl bg-emerald-400 px-5 py-3 text-sm font-black text-white shadow-[0_0_25px_rgba(52,211,153,0.35)] hover:bg-emerald-300 transition"
              >
                Start Free Preview
              </button>

              <a
                href="https://wa.me/447359062464?text=Hi%20NexoraX%2C%20I%20saw%20the%20TikTok%20launch%20offer%20and%20want%20help%20with%20a%20digital%20system."
                target="_blank"
                rel="noreferrer"
                className="rounded-xl border border-cyan-400/40 bg-cyan-500/10 px-5 py-3 text-sm font-black text-white hover:bg-cyan-500/20 transition"
              >
                WhatsApp NexoraX
              </a>

              <button
                type="button"
                onClick={() => { setStep(4); setNotice("Callback request selected. Add your name, phone and email, then send the brief to NexoraX."); setTimeout(() => document.getElementById("smart-quote-business-form")?.scrollIntoView({ behavior: "smooth", block: "start" }), 80); }}
                className="rounded-xl border border-yellow-400/50 bg-yellow-500/15 px-5 py-3 text-sm font-black text-yellow-100 shadow-[0_0_22px_rgba(250,204,21,0.18)] hover:bg-yellow-500/25 transition"
              >
                Request Callback
              </button>
            </div>
          </div>
        </section>

<div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <section id="smart-quote-business-form" className="rounded-[2rem] border border-zinc-800 bg-black/60 p-6 shadow-[0_0_40px_rgba(168,85,247,0.08)] backdrop-blur-xl">
            {notice && (
              <div className="mb-5 rounded-2xl border border-pink-500/30 bg-pink-950/20 p-4 text-sm text-pink-100">
                {notice}
              </div>
            )}

            {step === 1 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Step 1</p>
                <h2 className="mt-2 text-2xl font-black">Business information</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Business / page name if known" value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} />
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Industry / niche e.g. takeaway, builder, salon" value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} />
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Town / city" value={form.town} onChange={(e) => setForm({ ...form, town: e.target.value })} />
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Website / Facebook / TikTok / Instagram link" value={form.currentWebsite} onChange={(e) => setForm({ ...form, currentWebsite: e.target.value })} />
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400 md:col-span-2" placeholder="Extra link if you have one" value={form.socialLink} onChange={(e) => setForm({ ...form, socialLink: e.target.value })} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Step 2</p>
                <h2 className="mt-2 text-2xl font-black">What do you want NexoraX to build?</h2>
                <div className="mt-6 grid gap-3 md:grid-cols-3">
                  {SERVICE_OPTIONS.map((service) => (
                    <button
                      key={service}
                      type="button"
                      onClick={() => toggleService(service)}
                      className={`rounded-2xl border p-4 text-left text-sm font-bold transition-all ${
                        form.services.includes(service)
                          ? 'border-cyan-400 bg-cyan-950/30 text-white shadow-[0_0_18px_rgba(0,240,255,0.14)]'
                          : 'border-zinc-800 bg-black/50 text-slate-100 hover:border-purple-500/40'
                      }`}
                    >
                      {service}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Step 3</p>
                <h2 className="mt-2 text-2xl font-black">Budget, urgency and goals</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <select className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" value={form.budget} onChange={(e) => setForm({ ...form, budget: e.target.value })}>
                    <option>£49 audit only</option>
                    <option>£99 social / QR starter pack</option>
                    <option>£199–£499 starter budget</option>
                    <option>£500–£999 growth budget</option>
                    <option>£1,000–£2,499 serious build</option>
                    <option>£2,500+ advanced project</option>
                    <option>Not sure yet</option>
                  </select>
                  <select className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })}>
                    <option>ASAP</option>
                    <option>This week</option>
                    <option>This month</option>
                    <option>Next 2–3 months</option>
                    <option>Planning stage</option>
                  </select>
                  <textarea className="min-h-[150px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-purple-400 md:col-span-2" placeholder="What are your main goals? More leads, bookings, app, online payments, better branding, automation..." value={form.goals} onChange={(e) => setForm({ ...form, goals: e.target.value })} />
                </div>
              </div>
            )}

            {step === 4 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Step 4</p>
                <h2 className="mt-2 text-2xl font-black">Contact details</h2>
                <div className="mt-6 grid gap-4 md:grid-cols-2">
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Your name" value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} />
                  <input className="rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                  <input className="h-[52px] self-start rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-cyan-400" placeholder="Phone number" data-fix="SMART_QUOTE_PHONE_HEIGHT_FIX" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                  <textarea className="min-h-[120px] rounded-xl border border-zinc-800 bg-black px-4 py-3 text-sm outline-none focus:border-purple-400" placeholder="What would you like improved?" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
                </div>
              </div>
            )}

            {step === 5 && (
              <div>
                <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">Result</p>
                <h2 className="mt-2 text-2xl font-black">Your NexoraX recommendation</h2>

                {submitted ? (
                  <div className="mt-6 rounded-3xl border border-cyan-500/30 bg-cyan-950/20 p-6">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200">Free preview request sent successfully</p>
                    <h3 className="mt-2 text-2xl font-black text-white">Reference: {submitted.reference}</h3>
                    <p className="mt-3 text-sm text-slate-100">
                      Your free preview request has been sent to NexoraX. We’ll review your link and prepare improvement ideas before asking for any payment.
                    </p>

                    <div className="mt-5 rounded-2xl border border-cyan-400/35 bg-cyan-950/10 p-4">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-cyan-200">
                        SMART_QUOTE_FINAL_TRACK_BUTTON_V2
                      </p>
                      <p className="mt-2 text-sm text-slate-100">
                        Save this reference and track your preview status at any time.
                      </p>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <a
                          href={`/client-preview?reference=${encodeURIComponent(submitted.reference)}&email=${encodeURIComponent(form.email)}`}
                          className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-white hover:bg-cyan-900/50"
                        >
                          Track My Preview
                        </a>

                        <a
                          href="/client-preview"
                          className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                        >
                          Client Preview Portal
                        </a>
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                      <a
                        href={`/client-preview?reference=${encodeURIComponent(submitted.reference)}&email=${encodeURIComponent(form.email)}`}
                        className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-white hover:bg-cyan-900/50"
                      >
                        Track My Preview
                      </a>

                      <a
                        href="/smart-quote"
                        className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40"
                      >
                        Submit Another Brief
                      </a>
                    </div>

                    <p className="mt-4 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      SMART_QUOTE_TRACK_PREVIEW_LINK_FIX
                    </p>
                  </div>
                ) : (
                  <div className="mt-6 rounded-3xl border border-purple-500/30 bg-purple-950/20 p-6">
                    <h3 className="text-2xl font-black text-white">{recommendation.mainService}</h3>
                    <p className="mt-3 text-slate-100">{recommendation.why}</p>
                  </div>
                )}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3">
              {step > 1 && (
                <button type="button" onClick={() => setStep(step - 1)} className="rounded-xl border border-zinc-700 px-5 py-3 font-mono text-xs uppercase tracking-widest text-slate-200 hover:bg-zinc-900">
                  Back
                </button>
              )}
              {step < 5 && (
                <button type="button" onClick={() => setStep(step + 1)} className="rounded-xl border border-cyan-500/40 bg-cyan-950/40 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-white hover:bg-cyan-900/50">
                  Next Step
                </button>
              )}
              <button type="button" onClick={() => setStep(5)} className="rounded-xl border border-purple-500/40 bg-purple-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-purple-100 hover:bg-purple-900/40">
                Get AI Recommendation
              </button>
              <button type="button" disabled={saving} onClick={() => submitBrief('New')} className="rounded-xl border border-pink-500/40 bg-pink-950/30 px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-pink-100 hover:bg-pink-900/40 disabled:opacity-50">
                Request Free Preview
              </button>
              <button type="button" disabled={saving} onClick={() => submitBrief('Preview Requested')} className="rounded-xl border border-cyan-400/40 bg-black px-5 py-3 font-mono text-xs font-black uppercase tracking-widest text-white hover:bg-cyan-950/40 disabled:opacity-50">
                Request Free Preview
              </button>
            </div>
          </section>

          <aside className="space-y-6">
            <section className="rounded-[2rem] border border-cyan-400/35 bg-black/60 p-6 backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">AI Recommendation Panel</p>
              <h3 className="mt-3 text-2xl font-black">{recommendation.mainService}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-100">{recommendation.why}</p>

              <div className="mt-5 grid grid-cols-2 gap-3">
                {[
                  ['Growth Score', recommendation.overall],
                  ['Online Presence', recommendation.onlineScore],
                  ['Conversion Ready', recommendation.conversionScore],
                  ['Automation Chance', recommendation.automationScore]
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
                    <p className="mt-2 text-2xl font-black text-cyan-200">{value}%</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[2rem] border border-purple-500/25 bg-black/60 p-6 backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-purple-200">NexoraX Offer Matrix</p>
              <div className="mt-4 space-y-3">
                {OFFER_MATRIX.map((offer) => (
                  <div key={offer.name} className="rounded-2xl border border-zinc-800 bg-black/70 p-4">
                    <p className="font-bold text-white">{offer.name}</p>
                    <p className="mt-1 text-xs text-slate-200">{offer.price}</p>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-[2rem] border border-cyan-400/35 bg-black/60 p-6 backdrop-blur-xl">
              <p className="font-mono text-xs uppercase tracking-[0.35em] text-cyan-200">What happens next?</p>
              <div className="mt-4 space-y-3 text-sm text-slate-100">
                <p>1. NexoraX reviews your brief.</p>
                <p>2. We identify the best digital system for your business.</p>
                <p>3. You can request a preview, callback or full proposal.</p>
                <p>4. If approved, we move your project into build and launch flow.</p>
              </div>
            </section>
          </aside>
        </div>
      </section>
    </main>
  );
}
